// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';
import SignUpTrialInfo from 'presentations/views/components/molecules/SignUpTrialInfo';
import Headline from 'presentations/views/components/atoms/Headline';
import {
  borderRadius,
  fontSize,
  fontWeight,
  isMediaMax,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import {
  blue02,
  error,
  gray01,
  gray03,
  gray04,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import PricingList from 'presentations/views/components/organisms/PricingList';
import { usePricingPage, Props } from './hooks';
import {
  getPlanName,
  IntervalType,
  PlanId,
} from 'domains/models/subscription/planId';
import PlanSwitchForm from '../../organisms/PlanSwitchForm';
import { latestPlanVersion } from 'domains/models/subscription/planVersion';
import { formatToDate } from 'infras/datetime';
import CancelQuestionDialog from '../../organisms/features/subscription/CancelQuestionDialog';
import PricingTable from 'presentations/views/components/organisms/PricingTable';
import CompanyList from 'presentations/views/components/organisms/service/ServiceCompanyList';
import {
  pricingEducationPath,
  pricingStartupPath,
} from 'presentations/routes/path';
import FaqListItem from './FaqListItem';

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state, actions } = usePricingPage(props);
  const isChangeAnnualToMonth =
    state.currentIntervalType === IntervalType.Annually &&
    state.intervalType === IntervalType.Monthly;
  const isMobile = isMediaMax('tablet');

  return (
    <ServicePageTemplate
      header={props.header}
      isVisibleSupport={true}
      // contentHeader={
      //   <FormContentHeader
      //     icon="clipboard-text-outline"
      //     title={t('title')}
      //     description={t('description')}
      //   />
      // }
      breadcrumbs={[]}
    >
      <PageContainer>
        <Container>
          {!state.isLoading && (
            <PricingToggleContainer>
              <PricingToggle>
                <PricingAnnual
                  isActive={state.intervalType === IntervalType.Annually}
                  onClick={actions.onClickPricingToggle}
                >
                  {t('Annually')}
                  <PricingDiscountText>{t('Discount')}</PricingDiscountText>
                </PricingAnnual>
                <PricingMonth
                  isActive={state.intervalType === IntervalType.Monthly}
                  onClick={actions.onClickPricingToggle}
                >
                  {t('Monthly')}
                </PricingMonth>
              </PricingToggle>
            </PricingToggleContainer>
          )}

          <PricingList
            isLogin={state.isLogin}
            planId={state.planId}
            planVersion={state.planVersion}
            canChange={state.canChange}
            onClickPrice={actions.onClickPrice}
            isLoading={state.isLoading}
            trialStartedAt={state.trialStartedAt}
            trialEndedAt={state.trialEndedAt}
            trialChannel={state.trialChannel}
            currency={state.currency}
            intervalType={state.intervalType}
            selectedPlanId={state.selectPlanId}
            currentIntervalType={state.currentIntervalType}
            isChangeAnnualToMonth={isChangeAnnualToMonth}
          />
          <ProDescription />
          <CompanyListContainer>
            <CompanyList />
          </CompanyListContainer>
        </Container>
        <CampaignWrappr>
          <CampaignListContainer>
            <CampaignContent>
              <CampaignTitle>{t('Campaign 1')}</CampaignTitle>
              <CampaignText>{t('Campaign 1 text')}</CampaignText>
              <CampaignApplyButton href={pricingStartupPath()}>
                {t('Learn more')}
              </CampaignApplyButton>
            </CampaignContent>

            <CampaignContent>
              <CampaignTitle>{t('Campaign 2')}</CampaignTitle>
              <CampaignText>{t('Campaign 2 text')}</CampaignText>
              <CampaignApplyButton href={pricingEducationPath()}>
                {t('Learn more')}
              </CampaignApplyButton>
            </CampaignContent>
          </CampaignListContainer>
        </CampaignWrappr>
        <Container>
          {!isMobile && (
            <PricingTableContainer>
              <PricingTable
                isLogin={state.isLogin}
                planId={state.planId}
                planVersion={state.planVersion}
                canChange={state.canChange}
                onClickPrice={actions.onClickPrice}
                isLoading={state.isLoading}
                trialStartedAt={state.trialStartedAt}
                trialEndedAt={state.trialEndedAt}
                trialChannel={state.trialChannel}
                currency={state.currency}
                intervalType={state.intervalType}
                currentIntervalType={state.currentIntervalType}
                isChangeAnnualToMonth={isChangeAnnualToMonth}
              />
            </PricingTableContainer>
          )}

          <FaqSection>
            <FaqSectionTitleContainer>
              <FaqSectionTitle variant="h2">{t('faqTitle')}</FaqSectionTitle>
              <FaqSectionSubTitle>{t('faqSubTitle')}</FaqSectionSubTitle>
            </FaqSectionTitleContainer>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <FaqListItem
                key={i}
                title={t(`faqTitles.${i}`)}
                description={t(`faqDescriptions.${i}`)}
              />
            ))}
          </FaqSection>
        </Container>
      </PageContainer>

      {state.downgradePlanId === PlanId.Free ? (
        <CancelQuestionDialog
          isOpen={state.downgradePlanId !== null}
          isLoading={state.isLoading}
          isLeave={false}
          planName={getPlanName(state.downgradePlanId, latestPlanVersion) ?? ''}
          endedAt={formatToDate(
            state.currentPeriodEndedAt?.setZone(state.userProfile?.timeZone!),
          )}
          onCancel={actions.onCloseDowngradeModal}
          onSubmit={actions.onSubmitDowngrade}
        />
      ) : state.downgradePlanId ? (
        <ConfirmationDialog
          isLoading={state.isLoading}
          isOpen={state.downgradePlanId !== null}
          onSubmit={actions.onSubmitDowngrade}
          onCancel={actions.onCloseDowngradeModal}
          submitLabel={t('Downgrade')}
          title={t('Downgrade?', {
            planName: getPlanName(state.downgradePlanId, latestPlanVersion),
          })}
          text={t('Downgrade? text', {
            planName: getPlanName(state.downgradePlanId, latestPlanVersion),
            endedAt: formatToDate(
              state.currentPeriodEndedAt?.setZone(state.userProfile?.timeZone!),
            ),
          })}
        />
      ) : null}
      {props.viewer && state.upgradePlanId && (
        <PlanSwitchForm
          onCancel={actions.onCancelUpgrade}
          planId={state.upgradePlanId}
          planVersion={latestPlanVersion}
          intervalType={state.intervalType}
          isOpen={state.upgradePlanId !== null}
          viewer={props.viewer}
          schedulePlanId={state.schedulePlanId}
          schedulePlanVersion={state.schedulePlanVersion}
        />
      )}
      {props.viewer && state.isCancelDowngradeModalOpen && (
        <ConfirmationDialog
          isLoading={state.isCancelDowngradeIsLoading}
          isOpen={state.isCancelDowngradeModalOpen}
          onSubmit={actions.onCancelDowngrade}
          onCancel={actions.onCloseCancelDowngradeModal}
          submitLabel={t('Yes')}
          cancelLabel={t('No')}
          title={t('Cancel downgrade?')}
          text={t('Cancel downgrade? text', {
            planName: getPlanName(
              state.schedulePlanId,
              state.schedulePlanVersion,
            ),
            periodEnd: formatToDate(state.currentPeriodEndedAt),
          })}
        />
      )}

      {state.selectPlanId && state.isErrorModalOpen && (
        <ConfirmationDialog
          isLoading={state.isLoading}
          isOpen={state.isErrorModalOpen}
          onCancel={actions.onCloseErrorModal}
          title={t('Update plan failed', {
            planName: getPlanName(state.selectPlanId, latestPlanVersion),
          })}
          text={
            <ErrorContainer>
              <ErrorMessage>{t('Update plan failed reason')}</ErrorMessage>
              <ErrorMessageList>
                {state.downgradeErrors.map((error) => (
                  <ErrorMessageListItem>{error}</ErrorMessageListItem>
                ))}
              </ErrorMessageList>
            </ErrorContainer>
          }
          cancelLabel={t('Close')}
        />
      )}
    </ServicePageTemplate>
  );
}

const PageContainer = styled.div`
  /* background-color: ${blue02}; */
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing(48)};
  margin: 0 auto;

  ${mediaMax.tablet} {
    padding: ${spacing(24)} ${spacing(12)};
  }
`;

const PricingToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${spacing(48)};
`;
const PricingToggle = styled.div`
  background-color: ${white};
  border-radius: 54px;
  display: flex;
  align-items: center;
  width: fit-content;
  padding: ${spacing(8)};
  border: 1px dotted ${gray04};
`;
const PricingMonth = styled.div<{ isActive: boolean }>`
  width: 120px;
  height: 50px;
  background-color: ${(props) => props.isActive && primary};
  color: ${(props) => props.isActive && whiteFix};
  border-radius: 54px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: ${fontWeight('bold')};
  cursor: pointer;
  line-height: 1.2;
`;
const PricingAnnual = styled(PricingMonth)``;

const PricingDiscountText = styled.div``;

const ErrorContainer = styled.div`
  text-align: left;
`;
const ErrorMessage = styled.div``;
const ErrorMessageList = styled.ul`
  margin-top: ${spacing(16)};
`;
const ErrorMessageListItem = styled.li`
  color: ${error};
  margin-left: 0;
  margin-bottom: ${spacing(4)};
  padding-left: 1em;
  &:before {
    content: '-';
    display: inline-block;
    width: 1em;
    margin-left: -1em;
    text-align: center;
  }
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const CompanyListContainer = styled.div``;

const CampaignListContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  ${mediaMax.tablet} {
    display: block;
  }
`;
const CampaignContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${spacing(32)} 0;
  :first-child {
    border-right: 1px solid ${gray03};
  }
  ${mediaMax.tablet} {
    :first-child {
      border-bottom: 1px solid ${gray03};
      border-right: 0;
    }
  }
`;
const CampaignTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(24)};
  margin-bottom: ${spacing(32)};
  ${mediaMax.tablet} {
    padding: 0 ${spacing(12)};
    font-size: ${fontSize(18)};
    margin-bottom: ${spacing(16)};
  }
`;
const CampaignText = styled.div`
  padding: 0 ${spacing(16)};
  margin-bottom: ${spacing(32)};
  text-align: center;
  ${mediaMax.tablet} {
    padding: 0 ${spacing(12)};
    margin-bottom: ${spacing(16)};
  }
`;
const CampaignApplyButton = styled.a`
  width: 240px;
  background-color: ${primary};
  color: ${white};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing(8)} ${spacing(12)};
  border-radius: ${borderRadius(6)};
  font-weight: ${fontWeight('bold')};
`;

const ProDescription = styled(SignUpTrialInfo)`
  margin-top: ${spacing(16)};
  background-color: transparent;
  padding: 0;
  color: ${gray01};
  text-align: center;

  ${mediaMax.smartphone} {
    text-align: left;
  }
`;

const CampaignWrappr = styled.div`
  padding: ${spacing(32)} 0;
  background-color: ${gray04};
`;

const PricingTableContainer = styled.div`
  margin-top: ${spacing(32)};
`;

const FaqSection = styled.div`
  margin-top: ${spacing(32)};
`;
const FaqSectionTitleContainer = styled.div`
  margin-bottom: ${spacing(32)};
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const FaqSectionTitle = styled(Headline)`
  margin-bottom: ${spacing(4)};
  font-size: ${fontSize(24)};
`;
const FaqSectionSubTitle = styled.p`
  font-weight: ${fontWeight('bold')};
`;
